import { ICollectionSmartContract } from "@novel/shared/interfaces/CollectionSmartContract";
import { getPaymasterData } from "@novel/shared/paymasterData";
import { retryWithBackoff } from "@novel/shared/utils/retryWithBackoff";
import { getIsTestnet } from "@novel/shared/utils/cryptoLinkUtils";
import { shortenCryptoAddress } from "@novel/shared/utils/shortenCryptoAddress";
import { SurfaceableError } from "@novel/shared/utils/SurfaceableError";
import { RelayProvider } from "@opengsn/provider";
import Web3 from "web3";
import type { TransactionReceipt, PromiEvent } from "web3-core";
export interface TransactionExecConfig {
    methodWithArgsSupplied: any; // unfortunately Web3 library has as any :(
    cryptoWalletAddress: string;
    onTransactionHash: (transactionHash: string) => void;
}

const cacheMap = new Map<Web3, Promise<Web3>>();

export async function getCachedGsnProvider(
    web3Instance: Web3,
    collectionSmartContract: ICollectionSmartContract,
): Promise<Web3> {
    if (!cacheMap.has(web3Instance)) {
        const isTestnet = getIsTestnet(collectionSmartContract.blockchain);
        const config = getPaymasterData(isTestnet);
        try {
            const providerGSN = await RelayProvider.newProvider({
                provider: web3Instance.currentProvider as any,
                config: {
                    paymasterAddress: config.paymasterAddress,
                    relayLookupWindowBlocks: 1100,
                    relayRegistrationLookupBlocks: 1100,
                    pastEventsQueryMaxPageSize: 799,
                    gasPriceFactorPercent: 150,
                    maxRelayNonceGap: 10,
                    maxViewableGasLimit: 2500000,
                    preferredRelays: [
                        `${
                            isTestnet
                                ? `https://mumbai1-220.relays.opengsn.org/gsn1`
                                : `https://matic1-220.relays.opengsn.org/gsn1`
                        }`,
                    ],
                    loggerConfiguration: {
                        logLevel: "debug",
                    },
                },
            }).init();
            cacheMap.set(
                web3Instance,
                retryWithBackoff(async () => new Web3(providerGSN as any)),
            );
        } catch (err) {
            console.log("error creating GSN provider", err);
            throw err;
        }
    }

    return cacheMap.get(web3Instance)!;
}

const NUMBER_OF_TIMES_TO_RETRY = 3;
const TIME_BETWEEN_RETRIES = 1500;
const SIGNATURE_ERROR_STRING = "Failed to sign relayed transaction";
const ADDRESS_NOT_AUTHORIZED_ERROR_STRING = "has not been authorized";
const TIME_OUT_STRING = "Timed out but likely succeeded";
export async function execWeb3SendWithGsn(
    web3Provider: Web3,
    collectionSmartContract: ICollectionSmartContract,
    transactionExecConfig: TransactionExecConfig,
    retryCount = 0,
): Promise<TransactionReceipt> {
    const gsnWeb3ProviderPromise = getCachedGsnProvider(web3Provider, collectionSmartContract);

    // get latest nonce, for some reason required to do a transaction
    const noncePromise = retryWithBackoff(() =>
        web3Provider.eth.getTransactionCount(transactionExecConfig.cryptoWalletAddress, "latest"),
    );
    const gasPricePromise = retryWithBackoff(
        async () => `${(await web3Provider.eth.getGasPrice()).toString()}`,
    );
    const gasLimitPromise = retryWithBackoff<string>(
        async () =>
            `${Math.ceil(
                +(await transactionExecConfig.methodWithArgsSupplied.estimateGas({
                    from: transactionExecConfig.cryptoWalletAddress,
                })),
            )}`,
    );

    try {
        const [nonce, gasPrice, gasLimit, gsnWeb3Provider] = await Promise.all([
            noncePromise,
            gasPricePromise,
            gasLimitPromise,
            gsnWeb3ProviderPromise,
        ]);

        let hasTransactionBeenEmitted = false;
        const result = await promisifySendTransaction(
            gsnWeb3Provider.eth.sendTransaction({
                from: transactionExecConfig.cryptoWalletAddress,
                to: collectionSmartContract.address,
                nonce,
                gasPrice, // add 50% to gas price to ensure GSN doesn't block based on fluctuations
                gas: gasLimit, // add 50% to gas price to ensure GSN doesn't block based on fluctuations,
                data: transactionExecConfig.methodWithArgsSupplied.encodeABI(),
            }) as any,
            (transactionHash: string) => {
                hasTransactionBeenEmitted = true;
                return transactionExecConfig.onTransactionHash(transactionHash);
            },
        );

        console.log("result ", result);

        if (!hasTransactionBeenEmitted) {
            if (!result.transactionHash) {
                throw new Error("temporary mint error");
            }

            // it appears that sometimes the transaction succeeds but the hash is never emitted
            transactionExecConfig.onTransactionHash(result.transactionHash.toLowerCase());
        }

        return result;
    } catch (e: any) {
        console.log("error from GSN ", e);
        // exit if user chose not to sign transaction
        const errorMessage = e.message?.toLowerCase();
        const isUserClosedPopupError = errorMessage?.includes("user ");
        const isNotAuthorizedError = errorMessage?.includes(
            ADDRESS_NOT_AUTHORIZED_ERROR_STRING.toLowerCase(),
        );
        const isSignatureError = errorMessage?.includes(SIGNATURE_ERROR_STRING.toLowerCase());
        const shouldExitWithoutRetry =
            isUserClosedPopupError ||
            isSignatureError ||
            isNotAuthorizedError ||
            errorMessage?.includes(TIME_OUT_STRING.toLowerCase());

        if (isSignatureError) {
            // If we run into signature error, provider can't be recovered just by retrying
            // So we need to erase cached providers so that when user tries again, provider is created from scratch
            cacheMap.clear();
        }

        if (!shouldExitWithoutRetry && retryCount < NUMBER_OF_TIMES_TO_RETRY) {
            await new Promise((resolve) => setTimeout(resolve, TIME_BETWEEN_RETRIES));
            return execWeb3SendWithGsn(
                web3Provider,
                collectionSmartContract,
                transactionExecConfig,
                retryCount + 1,
            );
        }

        if (isNotAuthorizedError) {
            throw new SurfaceableError(
                `The wallet you are attempting to sign with has not been authorized for this address ${shortenCryptoAddress(
                    transactionExecConfig.cryptoWalletAddress,
                )}.`,
            );
        }
        if (isUserClosedPopupError) {
            throw new SurfaceableError("User denied message signature.");
        }
        if (isSignatureError) {
            throw new SurfaceableError(
                "Error signing the request. Please, refresh the page and try again.",
            );
        }

        throw e;
    }
}

const TIMEOUT_HANDLING_HANG_CASE = 10000; // 10 seconds
function promisifySendTransaction(
    sendTransactionPromiEvent: PromiEvent<TransactionReceipt>,
    onTransactionHash: (transactionHash: string) => void,
): Promise<TransactionReceipt> {
    return new Promise((resolve, reject) => {
        let hasCompleted = false;
        let hasTimedOut = false;

        let transactionHashHappened = false;
        sendTransactionPromiEvent.once("transactionHash", (transactionHash) => {
            transactionHashHappened = true;
            onTransactionHash(transactionHash.toLowerCase());
            if (hasTimedOut && !hasCompleted) {
                hasCompleted = true;
                reject(new SurfaceableError("Timed out but likely succeeded"));
            }
        });

        setTimeout(() => {
            hasTimedOut = true;
            if (!hasCompleted && transactionHashHappened) {
                hasCompleted = true;
                reject(new SurfaceableError("Timed out but likely succeeded"));
            }
        }, TIMEOUT_HANDLING_HANG_CASE);

        sendTransactionPromiEvent
            .then((result) => {
                if (!hasCompleted) {
                    hasCompleted = true;
                    resolve(result);
                }
            })
            .catch((e) => {
                if (!hasCompleted) {
                    hasCompleted = true;
                    reject(e);
                }
            });
    });
}
